import React, { useEffect, useRef } from "react";
import { View } from "react-native";

interface ListProps<T> {
  data: T[];
  renderItem: ({ item, index }: { item: T; index: number }) => React.ReactNode;
  keyExtractor: (item: T, index: number) => string;
  onEndReached?: () => void;
  onEndReachedThreshold?: number;
}

function List<T>({
  data,
  renderItem,
  keyExtractor,
  onEndReached,
  onEndReachedThreshold = 0.5,
}: ListProps<T>) {
  const observerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!onEndReached) return;

    const options = {
      root: null,
      rootMargin: `${onEndReachedThreshold * 100}% 0px`,
      threshold: 0.1,
    };

    const observer = new IntersectionObserver((entries) => {
      const [target] = entries;
      if (target.isIntersecting) {
        onEndReached();
      }
    }, options);

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => {
      if (observerRef.current) {
        observer.unobserve(observerRef.current);
      }
    };
  }, [onEndReached, onEndReachedThreshold]);

  return (
    <View>
      {data.map((item, index) => (
        <View key={keyExtractor(item, index)}>
          {renderItem({ item, index })}
        </View>
      ))}
      <div ref={observerRef} style={{ height: 10, width: "100%" }} />
    </View>
  );
}

export default List;
